"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import { calcArea, type AreaResult, type AreaShape, type AreaUnit } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const SHAPE_OPTIONS = [
  { value: "rectangle", label: "Rectangle / Square Room" },
  { value: "triangle", label: "Triangle" },
  { value: "circle", label: "Circle" },
  { value: "trapezoid", label: "Trapezoid" },
];

const UNIT_OPTIONS = [
  { value: "ft", label: "Feet (ft)" },
  { value: "m", label: "Meters (m)" },
  { value: "in", label: "Inches (in)" },
  { value: "cm", label: "Centimeters (cm)" },
  { value: "yd", label: "Yards (yd)" },
];

const UNIT_LABELS: Record<AreaUnit, string> = {
  ft: "ft²",
  m: "m²",
  in: "in²",
  cm: "cm²",
  yd: "yd²",
};

export default function CalculatorClient() {
  const [shape, setShape] = useState<AreaShape>("rectangle");
  const [unit, setUnit] = useState<AreaUnit>("ft");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [base, setBase] = useState("");
  const [triHeight, setTriHeight] = useState("");
  const [diameter, setDiameter] = useState("");
  const [base1, setBase1] = useState("");
  const [base2, setBase2] = useState("");
  const [trapHeight, setTrapHeight] = useState("");
  const [result, setResult] = useState<AreaResult | null>(null);

  function reset() { setResult(null); }

  function handleCalculate() {
    const res = calcArea({
      shape,
      unit,
      length: parseFloat(length) || 0,
      width: parseFloat(width) || 0,
      base: parseFloat(base) || 0,
      triangleHeight: parseFloat(triHeight) || 0,
      diameter: parseFloat(diameter) || 0,
      base1: parseFloat(base1) || 0,
      base2: parseFloat(base2) || 0,
      trapezoidHeight: parseFloat(trapHeight) || 0,
    });
    setResult(res);
  }

  const unitLabel = UNIT_LABELS[unit];

  return (
    <CalculatorShell
      name="Square Footage Calculator"
      description="Calculate area of any shape in square feet, square meters, and square yards — your starting point for all material estimates."
      icon="area"
      category="diy"
      accentColor="#14b8a6"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Estimates are based on simple geometric shapes. Complex angles or curves should be measured in smaller sections for accuracy."
      proTip="Always add a 10% waste buffer to your square footage when ordering flooring, tile, or deck boards."
      inputsPanel={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <SelectField id="area-shape" label="Shape" value={shape} onChange={(e) => { setShape(e.target.value as AreaShape); reset(); }} options={SHAPE_OPTIONS} />
            <SelectField id="area-unit" label="Unit of Measure" value={unit} onChange={(e) => { setUnit(e.target.value as AreaUnit); reset(); }} options={UNIT_OPTIONS} />
          </div>

          {shape === "rectangle" && (
            <div className="grid grid-cols-2 gap-3">
              <InputField id="area-length" label="Length" unit={unit} type="number" min="0" step="0.1" placeholder="20" value={length} onChange={(e) => { setLength(e.target.value); reset(); }} />
              <InputField id="area-width" label="Width" unit={unit} type="number" min="0" step="0.1" placeholder="15" value={width} onChange={(e) => { setWidth(e.target.value); reset(); }} />
            </div>
          )}
          {shape === "triangle" && (
            <div className="grid grid-cols-2 gap-3">
              <InputField id="area-base" label="Base" unit={unit} type="number" min="0" step="0.1" placeholder="10" value={base} onChange={(e) => { setBase(e.target.value); reset(); }} />
              <InputField id="area-tri-height" label="Height" unit={unit} type="number" min="0" step="0.1" placeholder="8" value={triHeight} onChange={(e) => { setTriHeight(e.target.value); reset(); }} />
            </div>
          )}
          {shape === "circle" && (
            <InputField id="area-diameter" label="Diameter" unit={unit} type="number" min="0" step="0.1" placeholder="12" value={diameter} onChange={(e) => { setDiameter(e.target.value); reset(); }} hint="Tip: Diameter = 2 × radius" />
          )}
          {shape === "trapezoid" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <InputField id="area-base1" label="Base 1 (top)" unit={unit} type="number" min="0" step="0.1" placeholder="8" value={base1} onChange={(e) => { setBase1(e.target.value); reset(); }} />
                <InputField id="area-base2" label="Base 2 (bottom)" unit={unit} type="number" min="0" step="0.1" placeholder="12" value={base2} onChange={(e) => { setBase2(e.target.value); reset(); }} />
              </div>
              <InputField id="area-trap-height" label="Height" unit={unit} type="number" min="0" step="0.1" placeholder="6" value={trapHeight} onChange={(e) => { setTrapHeight(e.target.value); reset(); }} />
            </>
          )}
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <ResultCard label={`Area in ${unitLabel}`} value={result.areaNative.toLocaleString()} unit={unitLabel} highlight size="lg" />
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Square Feet" value={result.areaSqFt.toLocaleString()} unit="ft²" accent />
              <ResultCard label="Square Meters" value={result.areaSqM.toLocaleString()} unit="m²" accent />
            </div>
            <ResultCard label="Square Yards" value={result.areaSqYd.toLocaleString()} unit="yd²" sub="Useful for carpet & fabric calculations" />
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Square Footage Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Accurate area measurement prevents costly material shortages or wasteful overages. In residential framing, structural spacing typically uses <strong>{CALCULATOR_SPECS.area.buyingGuide.standardStudSpacing.value}-inch</strong> centers for studs, which influences <Link href="/articles/how-much-paint-do-you-need-per-room/" className="text-[var(--accent)] hover:underline font-semibold">drywall and insulation panel layouts</Link>. Ceiling heights are standard at <strong>{CALCULATOR_SPECS.area.buyingGuide.standardCeilingHeight.value} feet</strong> under the International Residential Code (IRC). Always convert dimensions to a single unit (such as decimal feet) before multiplying to ensure your final <Link href="/articles/how-much-concrete-do-i-need-guide/" className="text-[var(--accent)] hover:underline font-semibold">square footage</Link> or square meter results are mathematically correct.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Use gross area when you are budgeting early and net area when materials are actually ordered. Paint, drywall, flooring, roofing, siding, and insulation all treat openings and waste differently, so the same room can produce different order quantities depending on the trade.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Record measurements with labels, not just numbers. A simple sketch showing length, width, alcoves, closets, stairs, fireplaces, columns, and built-ins prevents mistakes when you return later to order materials or compare contractor bids.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/area_material_guide.webp" alt="Architectural floor plan blueprint on drawing table with yellow measuring tape" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/area_site_prep.webp" alt="Contractor using a red laser distance measurer device on empty room floor" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Square Footage Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Real-world rooms are rarely perfect rectangles. Professional estimators divide complex or L-shaped spaces into simpler sub-sections (rectangles, triangles, or trapezoids) and calculate them individually. For triangular sections with irregular side lengths, trade contractors utilize <strong>{CALCULATOR_SPECS.area.sitePrep.triangulationFormula.value}</strong> to find precise area. When estimating materials, always measure and deduct major permanent obstructions such as masonry fireplaces, built-in cabinets, or stairwells to calculate net surface area.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Measure at finished surfaces when you are ordering finishes, and measure framing or rough openings when you are planning construction. Baseboards, drywall thickness, casing, and out-of-square walls can make finish dimensions slightly different from framing dimensions.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            For irregular spaces, take diagonal checks across rectangles. If the diagonals are not equal, the room is out of square and flooring, tile, or ceiling grids may need more waste even when the square footage is correct.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.area.industryStandards.minHabitableArea.standardCode}
        sourceName={CALCULATOR_SPECS.area.industryStandards.minHabitableArea.sourceName}
        sourceUrl={CALCULATOR_SPECS.area.industryStandards.minHabitableArea.sourceUrl}
        paragraphs={[
          <p key="p1">
            Building codes enforce strict space constraints to guarantee that homes remain safe, well-ventilated, and functional. Under <b>{CALCULATOR_SPECS.area.industryStandards.minHabitableArea.standardCode}</b> guidelines, every single-family home must feature at least one primary habitable room measuring no less than <b>{CALCULATOR_SPECS.area.industryStandards.minHabitableArea.value} square feet</b> of net floor space. Any other habitable rooms (with the exception of kitchens) are required to provide a minimum floor area of 70 square feet. These mandates ensure that occupants have sufficient physical space to live comfortably and exit safely during an emergency.
          </p>,
          <p key="p2">
            The code also dictates horizontal dimensions, stating that no habitable room can measure less than 7 feet in any direction. Furthermore, ceiling heights must reach at least 7 feet across the required floor area, though exceptions exist for sloped ceilings in attics. When planning home additions, conversions, or layout updates, builders and homeowners must calculate these net square footage values carefully to secure municipal permits and ensure the spaces legally qualify as habitable bedrooms or living areas.
          </p>,
          <p key="p3">
            Square footage used for material ordering is not always the same as real-estate living area. Closets, stairs, garages, unfinished basements, low-ceiling attic space, and exterior measurements may be handled differently depending on the purpose of the calculation.
          </p>,
          <p key="p4">
            When a measurement supports a permit, appraisal, lease, or sale listing, verify the local measurement method instead of relying on a material estimate. This calculator is best used for project planning and quantity takeoffs.
          </p>
        ]}
        keyRequirements={[
          "Primary habitable room must be at least 120 square feet.",
          "Other habitable rooms must be at least 70 square feet.",
          "No habitable room may be less than 7 feet in any horizontal dimension.",
          "Ceiling height must be at least 7 feet across the required floor area.",
          "Use gross area for early budgeting and net area for trade-specific orders.",
          "Split irregular spaces into simple shapes and label each measurement.",
          "Do not treat material square footage as appraised living area without verification."
        ]}
      />

      <FaqSection calculatorId="area" />
      <RelatedCalculators calculatorId="area" />
    </CalculatorShell>
  );
}
